import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Pencil, Trash2, Phone, Mail, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGUAGES = [
  { value: 'english', label: 'English', flag: 'EN' },
  { value: 'finnish', label: 'Finnish', flag: 'FI' },
  { value: 'spanish', label: 'Spanish', flag: 'ES' },
  { value: 'german', label: 'German', flag: 'DE' },
];
const LANG_COLORS = {
  english: 'bg-[#007AFF]/10 text-[#007AFF]',
  finnish: 'bg-[#00A3FF]/10 text-[#00A3FF]',
  spanish: 'bg-[#FFB800]/10 text-[#FFB800]',
  german: 'bg-[#FF3B30]/10 text-[#FF3B30]',
};
const getLangLabel = (v) => LANGUAGES.find(l => l.value === v)?.flag || 'EN';

const emptyContact = { name: '', email: '', phone: '', business_ids: [], service: '', notes: '', tags: [], subscribed: true, id_number: '', products: [], language: 'english' };

export default function ContactsPage() {
  const { hasRole } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [lists, setLists] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterBiz, setFilterBiz] = useState('all');
  const [filterLang, setFilterLang] = useState('all');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyContact);
  const [importOpen, setImportOpen] = useState(false);
  const [importBiz, setImportBiz] = useState('');
  const [importList, setImportList] = useState('');
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const fetchContacts = useCallback(async () => {
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (filterBiz && filterBiz !== 'all') params.business_id = filterBiz;
      if (filterLang && filterLang !== 'all') params.language = filterLang;
      const res = await axios.get(`${API}/contacts`, { params, withCredentials: true });
      setContacts(res.data.contacts);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
  }, [page, search, filterBiz, filterLang]);

  const fetchBusinesses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/businesses`, { withCredentials: true });
      setBusinesses(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchLists = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lists`, { withCredentials: true });
      setLists(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);
  useEffect(() => { fetchBusinesses(); fetchLists(); }, [fetchBusinesses, fetchLists]);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/contacts/${editing}`, form, { withCredentials: true });
        toast.success('Contact updated');
      } else {
        await axios.post(`${API}/contacts`, form, { withCredentials: true });
        toast.success('Contact created');
      }
      setOpen(false); setEditing(null); setForm(emptyContact);
      fetchContacts();
    } catch (e) { toast.error('Failed to save contact'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API}/contacts/${id}`, { withCredentials: true });
      toast.success('Contact deleted');
      fetchContacts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const openEdit = (c) => {
    // Backward compat: old contacts have business_id (string), new ones have business_ids (array)
    const bizIds = c.business_ids || (c.business_id ? [c.business_id] : []);
    setForm({ name: c.name, email: c.email, phone: c.phone, business_ids: bizIds, service: c.service, notes: c.notes, tags: c.tags || [], subscribed: c.subscribed, id_number: c.id_number || '', products: c.products || [], language: c.language || 'english' });
    setEditing(c.contact_id);
    setOpen(true);
  };

  const handleImport = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Please select a file'); return; }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const queryParts = [];
      if (importBiz) queryParts.push(`business_id=${importBiz}`);
      if (importList) queryParts.push(`list_id=${importList}`);
      const qs = queryParts.length ? `?${queryParts.join('&')}` : '';
      const res = await axios.post(`${API}/contacts/import${qs}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      let msg = `Imported ${res.data.imported} contacts (${res.data.skipped} skipped)`;
      if (res.data.list_added) msg += `, ${res.data.list_added} added to list`;
      toast.success(msg);
      setImportOpen(false);
      setImportBiz('');
      setImportList('');
      fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const handleExport = async () => {
    try {
      const params = filterBiz && filterBiz !== 'all' ? `?business_id=${filterBiz}` : '';
      const res = await axios.get(`${API}/contacts/export${params}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed. Super Admin access required.');
    }
  };

  const getBizNames = (ids) => {
    if (!ids || ids.length === 0) return '—';
    return ids.map(id => businesses.find(b => b.business_id === id)?.name || id).join(', ');
  };
  const toggleFormBusiness = (bid) => {
    setForm(p => ({
      ...p,
      business_ids: p.business_ids.includes(bid)
        ? p.business_ids.filter(x => x !== bid)
        : [...p.business_ids, bid]
    }));
  };

  return (
    <div className="flex-1 p-8" data-testid="contacts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>Contacts</h1>
          <p className="text-[#B0B0B0] text-sm mt-1">{total} total contacts</p>
        </div>
        <div className="flex gap-2">
          {/* Import */}
          <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportBiz(''); setImportList(''); } }}>
            <DialogTrigger asChild>
              <Button data-testid="import-contacts-btn" className="bg-transparent border border-[rgba(255,255,255,0.12)] text-white hover:border-[#00FF99] hover:text-[#00FF99]">
                <Upload size={16} className="mr-2" /> Import
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>Import Contacts</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-sm text-[#B0B0B0]">
                  Upload a CSV or XLSX file with columns: <span className="text-white">name, email, phone, service, notes, language</span>
                </p>
                <p className="text-xs text-[#808080]">
                  Language values: english, finnish, spanish, german (defaults to english if not specified)
                </p>
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Assign to Business (optional)</Label>
                  <Select value={importBiz} onValueChange={setImportBiz}>
                    <SelectTrigger data-testid="import-business-select" className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1">
                      <SelectValue placeholder="Select business" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                      {businesses.map(b => (
                        <SelectItem key={b.business_id} value={b.business_id} className="text-white hover:bg-[#1A1A1A]">{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Assign to Mailing List (optional)</Label>
                  <Select value={importList} onValueChange={setImportList}>
                    <SelectTrigger data-testid="import-list-select" className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1">
                      <SelectValue placeholder="Select mailing list" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                      {lists.map(l => (
                        <SelectItem key={l.list_id} value={l.list_id} className="text-white hover:bg-[#1A1A1A]">{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">File (CSV or XLSX)</Label>
                  <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls"
                    data-testid="import-file-input"
                    className="w-full mt-1 text-sm text-[#B0B0B0] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#1A1A1A] file:text-white file:cursor-pointer hover:file:bg-[#00FF99] hover:file:text-[#0A0A0A]" />
                </div>
                <Button onClick={handleImport} disabled={importing} data-testid="import-submit"
                  className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
                  {importing ? 'Importing...' : 'Import Contacts'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Export - Super Admin only */}
          {hasRole('super_admin') && (
            <Button onClick={handleExport} data-testid="export-contacts-btn"
              className="bg-transparent border border-[rgba(255,255,255,0.12)] text-white hover:border-[#00FF99] hover:text-[#00FF99]">
              <Download size={16} className="mr-2" /> Export
            </Button>
          )}

          {/* Add Contact */}
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyContact); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-btn" className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] rounded-md">
                <Plus size={16} className="mr-2" /> Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>{editing ? 'Edit Contact' : 'New Contact'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Name</Label>
                  <Input data-testid="contact-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Full name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Email</Label>
                    <Input data-testid="contact-email-input" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="email@example.com" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Phone</Label>
                    <Input data-testid="contact-phone-input" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                      className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="+1 234 567 890" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest mb-2 block">Businesses</Label>
                    <div className="space-y-1 max-h-28 overflow-y-auto border border-[rgba(255,255,255,0.12)] rounded-md p-2">
                      {businesses.map(b => (
                        <label key={b.business_id} className="flex items-center gap-2 p-1 rounded hover:bg-[#1A1A1A] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.business_ids.includes(b.business_id)}
                            onChange={() => toggleFormBusiness(b.business_id)}
                            className="accent-[#00FF99]"
                            data-testid={`contact-business-check-${b.business_id}`}
                          />
                          <span className="text-sm text-white truncate">{b.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Language</Label>
                    <Select value={form.language || 'english'} onValueChange={v => setForm(p => ({ ...p, language: v }))}>
                      <SelectTrigger data-testid="contact-language-select" className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                        {LANGUAGES.map(l => (
                          <SelectItem key={l.value} value={l.value} className="text-white hover:bg-[#1A1A1A]">
                            {l.flag} — {l.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Service</Label>
                    <Input data-testid="contact-service-input" value={form.service} onChange={e => setForm(p => ({ ...p, service: e.target.value }))}
                      className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Service purchased" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">ID Number</Label>
                    <Input data-testid="contact-id-number" value={form.id_number} onChange={e => setForm(p => ({ ...p, id_number: e.target.value }))}
                      className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="National ID / Passport" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Notes</Label>
                  <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                    className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Additional notes" />
                </div>
                {/* Products / Insurances */}
                <div className="border-t border-[rgba(255,255,255,0.12)] pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Products / Insurances</Label>
                    <button type="button" onClick={() => setForm(p => ({ ...p, products: [...(p.products || []), { name: '', value: '' }] }))}
                      className="text-xs text-[#00FF99] hover:underline">+ Add</button>
                  </div>
                  {(form.products || []).map((prod, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <Input value={prod.name} onChange={e => {
                        const updated = [...form.products];
                        updated[idx] = { ...updated[idx], name: e.target.value };
                        setForm(p => ({ ...p, products: updated }));
                      }} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-sm flex-1" placeholder="Product name" />
                      <Input value={prod.value} onChange={e => {
                        const updated = [...form.products];
                        updated[idx] = { ...updated[idx], value: e.target.value };
                        setForm(p => ({ ...p, products: updated }));
                      }} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-sm w-24" placeholder="Value" type="number" />
                      <button type="button" onClick={() => setForm(p => ({ ...p, products: p.products.filter((_, i) => i !== idx) }))}
                        className="text-[#808080] hover:text-[#FF3B30] shrink-0 p-1">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {form.products?.length > 0 && (
                    <p className="text-xs text-[#808080] mt-1">
                      Total: ${form.products.reduce((s, p) => s + (parseFloat(p.value) || 0), 0).toFixed(2)}
                    </p>
                  )}
                </div>
                <Button data-testid="save-contact-btn" onClick={handleSave} className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
                  {editing ? 'Update Contact' : 'Create Contact'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#808080]" />
          <Input data-testid="contact-search-input" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 bg-transparent border-[rgba(255,255,255,0.12)] text-white" placeholder="Search contacts..." />
        </div>
        <Select value={filterBiz} onValueChange={v => { setFilterBiz(v); setPage(1); }}>
          <SelectTrigger data-testid="contact-filter-business" className="w-48 bg-transparent border-[rgba(255,255,255,0.12)] text-white">
            <SelectValue placeholder="All businesses" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
            <SelectItem value="all" className="text-white hover:bg-[#1A1A1A]">All businesses</SelectItem>
            {businesses.map(b => (
              <SelectItem key={b.business_id} value={b.business_id} className="text-white hover:bg-[#1A1A1A]">{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterLang} onValueChange={v => { setFilterLang(v); setPage(1); }}>
          <SelectTrigger data-testid="contact-filter-language" className="w-44 bg-transparent border-[rgba(255,255,255,0.12)] text-white">
            <SelectValue placeholder="All languages" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
            <SelectItem value="all" className="text-white hover:bg-[#1A1A1A]">All languages</SelectItem>
            {LANGUAGES.map(l => (
              <SelectItem key={l.value} value={l.value} className="text-white hover:bg-[#1A1A1A]">{l.flag} — {l.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[rgba(255,255,255,0.12)] hover:bg-transparent">
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Contact</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Business</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Language</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Service</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map(c => (
              <TableRow key={c.contact_id} className="border-b border-[rgba(255,255,255,0.06)] hover:bg-[#1A1A1A]">
                <TableCell className="text-white font-medium">{c.name}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    {c.email && <span className="text-xs text-[#B0B0B0] flex items-center gap-1"><Mail size={12} />{c.email}</span>}
                    {c.phone && <span className="text-xs text-[#B0B0B0] flex items-center gap-1"><Phone size={12} />{c.phone}</span>}
                  </div>
                </TableCell>
                <TableCell className="text-[#B0B0B0] text-sm">{getBizNames(c.business_ids)}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${LANG_COLORS[c.language] || LANG_COLORS.english}`} data-testid={`lang-badge-${c.contact_id}`}>
                    {getLangLabel(c.language)}
                  </span>
                </TableCell>
                <TableCell className="text-[#B0B0B0] text-sm">{c.service || '—'}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded ${c.subscribed !== false ? 'bg-[#00FF99]/10 text-[#00FF99]' : 'bg-[#FF3B30]/10 text-[#FF3B30]'}`}>
                    {c.subscribed !== false ? 'Subscribed' : 'Unsubscribed'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-[#1A1A1A] text-[#B0B0B0] hover:text-white transition-colors duration-200">
                      <Pencil size={14} />
                    </button>
                    {hasRole('admin') && (
                      <button onClick={() => handleDelete(c.contact_id)} className="p-1.5 rounded hover:bg-[#1A1A1A] text-[#B0B0B0] hover:text-[#FF3B30] transition-colors duration-200">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {contacts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-[#808080] py-8">
                  No contacts found. Add your first contact to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {total > 50 && (
        <div className="flex justify-between items-center mt-4">
          <p className="text-sm text-[#808080]">Page {page} of {Math.ceil(total / 50)}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="border-[rgba(255,255,255,0.12)] text-white hover:border-[#00FF99]">Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(p => p + 1)}
              className="border-[rgba(255,255,255,0.12)] text-white hover:border-[#00FF99]">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
